import React, { useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { getAvailableStatuses, getStatusDisplayName, DEFAULT_STATUSES } from '../constants/statusTypes';

// Sortable option item component
const SortableOptionItem = ({ option, index, onOptionChange, onDeleteOption }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: option.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="option-row"
    >
      <div className="drag-handle" {...attributes} {...listeners}>
        ⋮⋮
      </div>
      <input
        type="text"
        value={option.name}
        onChange={(e) => onOptionChange(index, 'name', e.target.value)}
        className="option-name-input"
        placeholder="Option name"
      />
      <select
        value={option.status}
        onChange={(e) => onOptionChange(index, 'status', e.target.value)}
        className="option-status-select"
      >
        {getAvailableStatuses().map(statusKey => (
          <option key={statusKey} value={statusKey}>
            {getStatusDisplayName(statusKey)}
          </option>
        ))}
      </select>
      <button
        type="button"
        onClick={() => onDeleteOption(index)}
        className="delete-option-btn"
      >
        Delete
      </button>
    </div>
  );
};

const ListMetricConfig = ({ 
  metricName, 
  unit, 
  statusConfig, 
  onStatusConfigChange 
}) => {
  const [newOption, setNewOption] = useState('');

  // Add sensors for drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleAddOption = () => {
    if (newOption.trim()) {
      const updatedOptions = [
        ...statusConfig.options,
        { 
          id: `option-${Date.now()}-${Math.random()}`, // Generate unique ID
          name: newOption.trim(), 
          status: DEFAULT_STATUSES.OPERATIONAL 
        }
      ];
      onStatusConfigChange({
        ...statusConfig,
        options: updatedOptions
      });
      setNewOption('');
    }
  };

  const handleOptionChange = (index, field, value) => {
    const updatedOptions = statusConfig.options.map((option, i) => 
      i === index ? { ...option, [field]: value } : option
    );
    onStatusConfigChange({
      ...statusConfig,
      options: updatedOptions
    });
  };

  const handleDeleteOption = (index) => {
    const updatedOptions = statusConfig.options.filter((_, i) => i !== index);
    onStatusConfigChange({
      ...statusConfig,
      options: updatedOptions
    });
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddOption();
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = statusConfig.options.findIndex(option => option.id === active.id);
      const newIndex = statusConfig.options.findIndex(option => option.id === over.id);

      const updatedOptions = arrayMove(statusConfig.options, oldIndex, newIndex);
      onStatusConfigChange({
        ...statusConfig,
        options: updatedOptions
      });
    }
  };

  // Ensure all options have IDs (for backward compatibility)
  const optionsWithIds = statusConfig.options.map((option, index) => ({
    ...option,
    id: option.id || `option-${index}-${option.name || 'unnamed'}`
  }));

  // Update statusConfig with IDs if any options are missing them
  React.useEffect(() => {
    const needsUpdate = statusConfig.options.some(option => !option.id);
    if (needsUpdate) {
      const updatedOptions = statusConfig.options.map((option, index) => ({
        ...option,
        id: option.id || `option-${index}-${option.name || 'unnamed'}`
      }));
      onStatusConfigChange({
        ...statusConfig,
        options: updatedOptions
      });
    }
  }, [statusConfig, onStatusConfigChange]);

  return (
    <div className="status-config">
      <h4>Status Configuration</h4>
      
      <div className="add-option-section">
        <div className="add-option-input">
          <input
            type="text"
            value={newOption}
            onChange={(e) => setNewOption(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Add option (e.g. Gym)"
          />
          <button 
            type="button" 
            onClick={handleAddOption}
            className="add-option-btn"
          >
            Add option
          </button>
        </div>
      </div>


      <div className="options-list">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={optionsWithIds.map(option => option.id)}
            strategy={verticalListSortingStrategy}
          >
            {optionsWithIds.map((option, index) => (
              <SortableOptionItem
                key={option.id}
                option={option}
                index={index}
                onOptionChange={handleOptionChange}
                onDeleteOption={handleDeleteOption}
              />
            ))}
          </SortableContext>
        </DndContext>
      </div>
    </div>
  );
};

export default ListMetricConfig;
